import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { AGE_GROUPS, createAptCategory } from './aptCategories'
import { apiErrorText } from './constants'

// "+ New category…" (2026-09-17): add an At Apartment category from inside a
// Category dropdown, e.g. "5+ Music" when Music launched. The catalogue dialogs
// show the form inline under the field; grid cells, which have no room, open it
// as a small dialog via useNewCategoryPrompt.

// The dropdown value that opens the form instead of being saved.
export const NEW_CATEGORY = '__new_category__'

const useStyles = makeStyles((theme) => ({
  panel: {
    border: `1px dashed ${theme.palette.primary.main}`,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    margin: theme.spacing(0.5, 0, 1.5)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    flexWrap: 'wrap'
  },
  subject: { flex: 1, minWidth: 160 },
  newItem: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderTop: `1px solid ${theme.palette.divider}`
  }
}))

/** The last option of a Category dropdown. Place it inside the Select. */
export const NewCategoryMenuItem = React.forwardRef((props, ref) => {
  const classes = useStyles()
  return (
    <MenuItem ref={ref} {...props} className={classes.newItem}>
      + New category…
    </MenuItem>
  )
})

/** Age group + subject -> "5+ Music". Calls onCreated(name) once saved. */
export const NewCategoryForm = ({ onCreated, onCancel, framed = true }) => {
  const classes = useStyles()
  const [ageGroup, setAgeGroup] = React.useState('5+')
  const [subject, setSubject] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const clean = subject.trim().replace(/\s+/g, ' ')

  const submit = async () => {
    if (!clean) {
      setError('Type the subject, e.g. Music.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const row = await createAptCategory(ageGroup, clean)
      setSaving(false)
      onCreated(row.name, row)
    } catch (e) {
      setSaving(false)
      setError(apiErrorText(e) || 'Could not add the category.')
    }
  }

  return (
    <Box className={framed ? classes.panel : undefined}>
      {framed && (
        <Typography variant="subtitle2" gutterBottom>
          New category
        </Typography>
      )}
      <div className={classes.row}>
        <Typography variant="caption" color="textSecondary">
          Age group
        </Typography>
        <ButtonGroup size="small" disableElevation>
          {AGE_GROUPS.map((g) => (
            <Button
              key={g}
              color="primary"
              variant={g === ageGroup ? 'contained' : 'outlined'}
              onClick={() => setAgeGroup(g)}
            >
              {g}
            </Button>
          ))}
        </ButtonGroup>
        <TextField
          className={classes.subject}
          id="apt-new-category-subject"
          autoFocus
          size="small"
          variant="outlined"
          label="Subject"
          placeholder="e.g. Music"
          value={subject}
          error={!!error}
          helperText={error || ' '}
          inputProps={{ maxLength: 25 }}
          onChange={(e) => setSubject(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
        />
      </div>
      <Typography variant="caption" color="textSecondary" component="div">
        {clean ? (
          <>
            Saved as <b>{`${ageGroup} ${clean}`}</b>, with its own budget for
            every apartment.
          </>
        ) : (
          'Adding the same subject for the other age group makes a separate category.'
        )}
      </Typography>
      <Box display="flex" justifyContent="flex-end" mt={1} style={{ gap: 8 }}>
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disableElevation
          onClick={submit}
          disabled={saving}
        >
          {saving ? 'Adding…' : 'Add category'}
        </Button>
      </Box>
    </Box>
  )
}

/**
 * For dropdowns with no room for the inline form (grid cells). Returns
 * [ask, dialog]: render `dialog` once, and call ask(onPicked) when the
 * dropdown's value is NEW_CATEGORY; onPicked(name) gets the new category.
 */
export const useNewCategoryPrompt = () => {
  const [onPicked, setOnPicked] = React.useState(null)
  const ask = React.useCallback((fn) => setOnPicked(() => fn), [])
  const close = () => setOnPicked(null)
  const dialog = (
    <Dialog open={!!onPicked} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>New category</DialogTitle>
      <DialogContent>
        {onPicked && (
          <NewCategoryForm
            framed={false}
            onCancel={close}
            onCreated={(name) => {
              const fn = onPicked
              close()
              fn(name)
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
  return [ask, dialog]
}
